import { Inject, Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { RuntimeCacheService } from '../runtime-cache.service';
import { plainToInstance } from 'class-transformer';
import { BackendErrorContainerRes } from '../../contracts/v0/cp';
import { Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { NotificationService } from '../notification-service';
import { catchError, finalize, map, Observable, throwError, timeout, TimeoutError } from 'rxjs';

const APP_XHR_TIMEOUT = 60000;

@Injectable({
    providedIn: 'root'
})
export class AppInterceptor implements HttpInterceptor {

    constructor(
        private runTimeCacheService: RuntimeCacheService,
        private notificationService: NotificationService,
        private router: Router,
        @Inject(L10N_LOCALE) public locale: L10nLocale,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(this.performRequest(req)).pipe(
            timeout(APP_XHR_TIMEOUT),
            map((res) => this.handleSuccessfulResponse(res)),
            catchError((err) => this.handleErrorResponse(err)),
            finalize(() => this.handleRequestCompleted())
        );
    }

    private performRequest(req: HttpRequest<any>): HttpRequest<any> {
        let headers: HttpHeaders = req.headers;
        const isFormData = req.body instanceof FormData;

        if (!headers.has('Content-Type') && req.body != null && !isFormData) {
            headers = headers.set('Content-Type', 'application/json');
        }

        if (this.runTimeCacheService.isLogged()) {
            headers = headers.set('Authorization', `Bearer ${this.runTimeCacheService.token}`);
        }

        if (!headers.has('Accept-Language')) {
            headers = headers.set('Accept-Language', this.locale.language);
        }

        return req.clone({ url: req.url, headers });
    }

    private handleSuccessfulResponse(event: HttpEvent<any>): HttpEvent<any> {
        if (event instanceof HttpResponse && event?.body?.response !== undefined) {
            return event.clone({ body: event.body.response });
        }
        return event;
    }

    private handleErrorResponse(errorResponse: unknown): Observable<never> {
        if (errorResponse instanceof TimeoutError) {
            this.runTimeCacheService.resetAuthState();
            this.router.navigate(['maintenance']).then();
            return throwError(() => errorResponse);
        }

        const httpError = errorResponse as HttpErrorResponse;

        if (httpError.status === 0) {
            this.runTimeCacheService.resetAuthState();
            this.router.navigate(['maintenance']).then();
            return throwError(() => httpError);
        }

        if (httpError.status === 401) {
            this.notificationService.closeAll();
            this.runTimeCacheService.logout(true);
        }

        const backendError = plainToInstance(
            BackendErrorContainerRes,
            httpError.error ?? {}
        ) as BackendErrorContainerRes;

        if (backendError?.exception) {
            this.notificationService.errorFromBackendError(backendError);
            return throwError(() => backendError);
        }

        return throwError(() => httpError);
    }

    private handleRequestCompleted(): void {
    }
}